// University of Iowa: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Iowa's Common Data Set 2025-2026 (CDS_2526.pdf), linked from the Office
//    of the Provost's Common Data Set page: C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table; C8 test policy for
//    Fall 2027; C21/C22 plans. Its G1/G5 cost rows are blank (Iowa ticked
//    the box saying 2026-2027 costs were not yet available), so the costs
//    come from Admissions instead.
//  - Iowa Admissions: First-Year Admissions (deadlines, fee), ACT and SAT
//    Test Scores ("Standardized Test and Admissions Process for Students
//    Applying for Fall 2027", with the decision timeline), and the
//    first-year admission pages for Liberal Arts and Sciences, Engineering,
//    Tippie College of Business and Nursing (the Nursing page names fall
//    2027).
//  - Iowa Admissions, Tuition and Estimated Costs: Living On Campus,
//    2026-27, Iowa resident and nonresident. Iowa lists tuition and fees as
//    one line; it includes the one-time $250 records fee for new students.
//  - College Scorecard, school id 153658 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Quirk: Admissions calls its November 2 deadline "Early Action" (decisions
// by late January), while the CDS answers No to having a nonbinding early
// action plan and gives no early counts; the page shows both.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Iowa Common Data Set 2025-2026',
        url: 'https://provost.uiowa.edu/data-and-reports/common-data-set',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Iowa Admissions, First-Year Admissions',
        url: 'https://admissions.uiowa.edu/apply/how-apply/first-year-admissions',
        read: '2026-10-01',
    },
    testing: {
        label: 'Iowa Admissions, ACT and SAT Test Scores (Fall 2027)',
        url: 'https://admissions.uiowa.edu/testing',
        read: '2026-10-01',
    },
    clas: {
        label: 'Iowa Admissions, College of Liberal Arts and Sciences First-Year Admissions',
        url: 'https://admissions.uiowa.edu/college-liberal-arts-and-sciences-first-year-admissions',
        read: '2026-10-01',
    },
    engineering: {
        label: 'Iowa Admissions, College of Engineering First-Year Admissions',
        url: 'https://admissions.uiowa.edu/college-engineering-first-year-admissions',
        read: '2026-10-01',
    },
    tippie: {
        label: 'Iowa Admissions, Tippie College of Business First-Year Admissions',
        url: 'https://admissions.uiowa.edu/tippie-college-business-first-year-admissions',
        read: '2026-10-01',
    },
    nursing: {
        label: 'Iowa Admissions, College of Nursing First-Year Admissions',
        url: 'https://admissions.uiowa.edu/college-nursing-first-year-admissions',
        read: '2026-10-01',
    },
    costs: {
        label: 'Iowa Admissions, Tuition and Estimated Costs',
        url: 'https://admissions.uiowa.edu/finances/estimated-costs-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?153658-University-of-Iowa',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-iowa',
    kbId: 'university_of_iowa',
    name: 'University of Iowa',
    shortName: 'Iowa',
    aliases: ['UIowa'],
    location: 'Iowa City, IA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Iowa brand color page); decoration only.
    branding: {
        primary: '#FFCD00',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_iowa_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 31454,
        admitted: 25131,
        enrolled: 5561,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 5892, admitted: 4984 },
            { group: 'Out-of-state', applied: 24060, admitted: 19510 },
            { group: 'International', applied: 1502, admitted: 637 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Iowa\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Iowa has no early decision plan. Admissions calls its November 2 deadline early action, with decisions by late January, but the Common Data Set reports no early action plan and no early counts, so there is no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'First-year students enter Liberal Arts and Sciences, Engineering, Business, Nursing, Public Health or Education. Each college sets its own bar:',
        rows: [
            { school: 'College of Liberal Arts and Sciences', requirement: 'The high school course requirements and a Regent Admission Index (RAI) of 245 or higher for Iowa residents, 255 or higher for other states. You can be admitted without a test score.', source: 'clas' },
            { school: 'College of Engineering', requirement: 'Direct admission with an RAI of 265 or higher and the course requirements, with math and science grades weighed most. You can be admitted without a test score. If not admitted directly, you start in Liberal Arts and Sciences as an Engineering Interest major and apply later.', source: 'engineering' },
            { school: 'Tippie College of Business', requirement: 'Direct admission to the BBA with the course requirements, a 26 ACT or 1230 SAT, and a 3.60 GPA. Meeting the courses and either the score or the GPA gets careful consideration, and you may be admitted without a score. Students in review learn their Tippie status by March 19.', source: 'tippie' },
            { school: 'College of Nursing', requirement: 'Up to 168 students win direct admission. Apply with a Nursing major by November 2. An ACT or SAT score is required: 24 ACT or 1160 SAT for Iowa residents, 26 or 1230 for others, with a 3.80 GPA. The supplemental application is due December 7, 2026, interviews run in January 2027, and decisions come in late February to early March.', source: 'nursing' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 2', decision: 'On or before late January', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'On or before early March', binding: false },
        ],
        note: 'Documents are due November 9 for Early Action and February 8 for Regular Decision. Decisions are made on a rolling basis. After February 1 you are admitted only case by case, as space allows, and are not eligible for merit scholarships.',
        source: 'testing',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Iowa is test-optional for general admission with a "no harm" policy: scores are used only if they help your admission or merit scholarship review. You can enter a score on the application or apply without one. Iowa does not superscore. Some programs need scores, Nursing direct admission among them. For scholarships, new scores must arrive by February 8, 2027, from tests taken by December 12, 2026.',
        source: 'testing',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by May 1, and notes that the Iowa Board of Regents made the option permanent.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [11971, 34247] },
            { item: 'Housing and food (on campus)', amounts: [14022, 14022] },
            { item: 'Books and supplies', amounts: [950, 950] },
            { item: 'Personal expenses', amounts: [3648, 3648] },
            { item: 'Transportation', amounts: [1254, 1254] },
        ],
        totals: [31845, 54121],
        note: 'For a full-time undergraduate living on campus, fall and spring, including the one-time $250 records fee for new students. Iowa calls these estimates. Business, engineering, nursing and some health science students pay more tuition.',
        source: 'costs',
        netPrice: {
            average: 22508,
            byIncome: [
                { income: '$0 to $30,000', amount: 15353 },
                { income: '$30,001 to $48,000', amount: 15374 },
                { income: '$48,001 to $75,000', amount: 17179 },
                { income: '$75,001 to $110,000', amount: 22278 },
                { income: 'Over $110,000', amount: 26721 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Iowa\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
